<template>
  <div class="font-sans max-w-screen-xl mx-auto p-5">
    <div class="flex justify-between items-center mb-5">
      <button @click="previousDay"
              class="bg-blue/90 hover:bg-blue font-title uppercase text-[24px] text-black border border-black px-4 py-2.5 cursor-pointer">
        &lt; Previous Day
      </button>
      <button @click="nextDay"
              class="bg-blue/90 hover:bg-blue font-title uppercase text-[24px] text-black border border-black px-4 py-2.5 cursor-pointer">
        Next Day &gt;
      </button>
    </div>

    <div class="mb-2.5">
      <div class="font-title text-[18px] text-center p-2.5 bg-gray-100 border border-gray-300">
        <div class="font-bold">{{ currentDay.name }}</div>
        <div class="font-swiza text-[15px]">{{ currentDay.date }}</div>
      </div>
    </div>

    <div>
      <div class="flex flex-col">
        <div
            v-for="slot in timeSlots"
            :key="`${currentDay.date}-${slot.time}`"
            class="border border-gray-300 m-0.5 p-2"
            :class="{
            'bg-green-800 text-white': (slotAvailable(currentDay.phpDate, slot.time) > 0 && slotAvailable(currentDay.phpDate, slot.time) >= 4),
            'bg-yellow text-black': (slotAvailable(currentDay.phpDate, slot.time) > 0 && slotAvailable(currentDay.phpDate, slot.time) <= 3),
            'bg-gray-500 text-white': (slotAvailable(currentDay.phpDate, slot.time) == 0)
          }"
        >
          <div class="flex justify-between items-center">
            <span class="font-bold">{{ slot.time }}</span>
            <span>Available: {{ slotAvailable(currentDay.phpDate, slot.time) }} / 15</span>
          </div>

          <!-- Bookings for this slot -->
          <div v-if="getBookingsForSlot(currentDay.phpDate, slot.time).length > 0" class="mt-2">
            <div class="text-sm font-semibold">Bookings:</div>
            <div
                v-for="(booking, index) in getBookingsForSlot(currentDay.phpDate, slot.time)"
                :key="index"
                class="bg-white text-black text-sm p-1 border-t border-gray-400 mt-1"
            >
              <div class="flex justify-between">
                <div class="">
                  <span>{{ booking.name }}</span>
                  <div class="text-xs">{{ booking.email }}</div>

                </div>
                <div class=" items-center flex flex-row gap-2">
                  <span>Group of {{ booking.groupSize }}</span>
                  <button
                      @click="openModifyBookingModal(booking, currentDay.phpDate, slot.time)"
                      class="h-full bg-yellow/80 hover:bg-yellow  uppercase font-medium px-2 py-1 rounded"
                  >
                    Modify
                  </button>
                  <button
                      v-if="!booking.checkedIn"
                      @click="checkInBooking(booking.id)"
                      class="h-full bg-green-600 hover:bg-green-700 text-white uppercase font-medium px-2 py-1 rounded"
                  >
                    Check In
                  </button>
                  <span v-else class="text-xs text-green-600 font-semibold">Checked In</span>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="mt-2 text-sm italic">No bookings for this slot</div>
        </div>
      </div>
    </div>
  </div>

  <!-- Modify Booking Modal -->
  <div v-if="showModifyBookingModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white p-4 shadow-lg max-w-md w-full">
      <div
          class="font-title px-2 sm:px-3 mb-4 pt-3 sm:pt-4 pb-2 sm:pb-3  bg-yellow border border-black text-[24px] sm:text-[34px]  leading-none uppercase">
        Modify booking
      </div>

      <p class="text-lg sm:text-xl font-bold mb-1">{{ formatCurrentDay }} at {{ selectedBooking ? selectedBooking.time : '' }}</p>

      <div v-if="selectedBooking" class="mb-4">
        <p><strong>Name:</strong> {{ selectedBooking.name }}</p>
        <p><strong>Email:</strong> {{ selectedBooking.email }}</p>
        <p><strong>Current Group Size:</strong> {{ selectedBooking.groupSize }}</p>
      </div>
      <div class="mb-4">
        <label for="newGroupSize" class="block text-sm font-medium text-gray-700 mb-1">New Group Size</label>
        <input
            type="number"
            id="newGroupSize"
            v-model="newGroupSize"
            min="0"
            max="15"
            class="w-full p-2 border border-gray-300 rounded"
        >
        <p v-if="modifyError" class="text-red-500 text-sm mt-1">{{ modifyError }}</p>
      </div>
      <div class="flex justify-between items-end gap-2">
        <button
            @click="closeModifyBookingModal"
            class=" text-black hover:underline "
        >
          Close without saving
        </button>
        <button
            @click="modifyBooking"
            :class="[
              newGroupSize === 0 
                ? 'bg-red-600 hover:bg-red-700 text-white' 
                : 'bg-blue/90 hover:bg-blue text-black',
              'font-title uppercase text-[24px] border border-black px-4 py-2.5'
            ]"
            :disabled="isModifying"
        >
          {{ isModifying ? 'Saving...' : (newGroupSize === 0 ? 'Cancel booking' : 'Save Changes') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';

// Current day (default to today)
const currentDate = ref(new Date());

// Booked slots tracking
const bookedSlots = ref([]);

// Modify booking modal state
const showModifyBookingModal = ref(false);
const selectedBooking = ref(null);
const newGroupSize = ref(1);
const modifyError = ref('');
const isModifying = ref(false);

// Get day name
function getDayName(date) {
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return dayNames[date.getDay()];
}

// Format date as 'Monday, 22 May 2025' or '22 May 2025' depending on addDayOfWeek
function formatDate(date, addDayOfWeek = false) {
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  return addDayOfWeek 
    ? `${getDayName(date)} ${date.getDate()} ${monthNames[date.getMonth()]} ${date.getFullYear()}`
    : `${date.getDate()} ${monthNames[date.getMonth()]} ${date.getFullYear()}`;
}

// Format date as YYYY-MM-DD for PHP
function formatDateForPHP(date) {
  return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
}

// Navigation functions
function previousDay() {
  const newDate = new Date(currentDate.value);
  newDate.setDate(newDate.getDate() - 1);
  currentDate.value = newDate;
  loadBookings();
}

function nextDay() {
  const newDate = new Date(currentDate.value);
  newDate.setDate(newDate.getDate() + 1);
  currentDate.value = newDate;
  loadBookings();
}

// Current day information
const currentDay = computed(() => {
  return {
    name: getDayName(currentDate.value),
    date: formatDate(currentDate.value, false),
    phpDate: formatDateForPHP(currentDate.value),
    fullDate: new Date(currentDate.value)
  };
});

// Format current day for display
const formatCurrentDay = computed(() => {
  return formatDate(currentDate.value, true);
});

// Generate time slots (26 slots of 15 minutes each, starting at 9:15 AM with 5-minute gaps)
const timeSlots = computed(() => {
  const slots = [];
  let hour = 9;
  let minute = 15;

  for (let i = 0; i < 26; i++) {
    // Format time as HH:MM
    const formattedHour = hour.toString().padStart(2, '0');
    const formattedMinute = minute.toString().padStart(2, '0');
    const time = `${formattedHour}:${formattedMinute}`;

    slots.push({time});

    // Increment time by 15 minutes + 5 minutes gap
    minute += 20;
    if (minute >= 60) {
      hour += Math.floor(minute / 60);
      minute %= 60;
    }
  }

  return slots;
});

// Check if a slot has bookings and return available spaces
function slotAvailable(date, time) {
  // Create a date object for the slot we're checking
  const [hours, minutes] = time.split(':').map(Number);

  // Check each booking
  for (const slot of bookedSlots.value) {
    // Normalize the date format
    let slotDate = slot.date;
    if (slotDate && typeof slotDate === 'string' && !slotDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
      try {
        const dateObj = new Date(slotDate);
        slotDate = formatDateForPHP(dateObj);
      } catch (e) {
        console.error('Error converting date:', e);
        continue; // Skip this slot if date conversion fails
      }
    }

    // Extract hours and minutes from the slot time (handling HH:MM:SS format)
    const slotTimeParts = slot.time.split(':');
    const slotHours = parseInt(slotTimeParts[0], 10);
    const slotMinutes = parseInt(slotTimeParts[1], 10);

    // Compare date and time (hours and minutes only)
    if (slotDate === date && slotHours === hours && slotMinutes === minutes) {
      return slot.available;
    }
  }

  return 15; // Default capacity if no booking found
}

// Get bookings for a specific slot
function getBookingsForSlot(date, time) {
  // Create a date object for the slot we're checking
  const [hours, minutes] = time.split(':').map(Number);

  // Check each booking
  for (const slot of bookedSlots.value) {
    // Normalize the date format
    let slotDate = slot.date;
    if (slotDate && typeof slotDate === 'string' && !slotDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
      try {
        const dateObj = new Date(slotDate);
        slotDate = formatDateForPHP(dateObj);
      } catch (e) {
        console.error('Error converting date:', e);
        continue; // Skip this slot if date conversion fails
      }
    }

    // Extract hours and minutes from the slot time (handling HH:MM:SS format)
    const slotTimeParts = slot.time.split(':');
    const slotHours = parseInt(slotTimeParts[0], 10);
    const slotMinutes = parseInt(slotTimeParts[1], 10);

    // Compare date and time (hours and minutes only)
    if (slotDate === date && slotHours === hours && slotMinutes === minutes) {
      // Return the emails array which contains objects with email, name, and groupSize
      return slot.emails || [];
    }
  }

  return []; // No bookings found for this slot
}

// Load bookings from the server
function loadBookings() {
  fetch('/actions/museum/booking/get-bookings?admin=1', {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    }
  })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {
        if (data.success) {
          bookedSlots.value = data.bookings;
          console.log('Loaded bookings:', bookedSlots.value);
        }
      })
      .catch(error => {
        console.error('Error loading bookings:', error);
      });
}

// Check in a booking
function checkInBooking(bookingId) {
  // Prepare data
  const data = {
    bookingId: bookingId
  };
  // Add CSRF token if needed
  data[csrfTokenName] = csrfTokenValue;

  // Make a fetch call to the Craft CMS controller
  fetch('/actions/museum/booking/check-in', {
    method: 'POST',
    headers: {
      'Content-type': 'application/json',
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    },
    body: JSON.stringify(data)
  })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {
        if (data.success) {
          console.log('Check-in successful:', data);
          // Reload bookings to update the display
          loadBookings();
        } else {
          console.error('Check-in failed:', data.error);
          alert('Failed to check in: ' + data.error);
        }
      })
      .catch(error => {
        console.error('Error checking in:', error);
        alert('Error checking in: ' + error.message);
      });
}

// Open the modify booking modal
function openModifyBookingModal(booking, date, time) {
  selectedBooking.value = booking;
  console.log(selectedBooking.value);
  selectedBooking.value.date = date;
  selectedBooking.value.time = time;
  newGroupSize.value = booking.groupSize;
  modifyError.value = '';
  showModifyBookingModal.value = true;
}

// Close the modify booking modal
function closeModifyBookingModal() {
  showModifyBookingModal.value = false;
  selectedBooking.value = null;
  newGroupSize.value = 1;
  modifyError.value = '';
}

// Modify a booking
function modifyBooking() {
  if (!selectedBooking.value) {
    return;
  }

  // Validate the new group size
  if (newGroupSize.value === undefined || newGroupSize.value === null || newGroupSize.value < 0) {
    modifyError.value = 'Group size must be at least 0';
    return;
  }

  isModifying.value = true;
  modifyError.value = '';

  // Prepare data
  const data = {
    bookingId: selectedBooking.value.id,
    newGroupSize: newGroupSize.value
  };
  // Add CSRF token if needed
  data[csrfTokenName] = csrfTokenValue;

  // Make a fetch call to the Craft CMS controller
  fetch('/actions/museum/booking/modify', {
    method: 'POST',
    headers: {
      'Content-type': 'application/json',
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    },
    body: JSON.stringify(data)
  })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {
        isModifying.value = false;
        if (data.success) {
          if (data.cancelled) {
            console.log('Booking cancellation successful:', data);
          } else {
            console.log('Booking modification successful:', data);
          }
          // Close the modal
          closeModifyBookingModal();
          // Reload bookings to update the display
          loadBookings();
        } else {
          console.error('Booking modification failed:', data.error);
          modifyError.value = data.error || 'Failed to modify booking';
        }
      })
      .catch(error => {
        isModifying.value = false;
        console.error('Error modifying booking:', error);
        modifyError.value = error.message || 'An error occurred while modifying the booking';
      });
}

// Load bookings when component is mounted
onMounted(() => {
  loadBookings();
});
</script>
